import type { ToolDefinition } from './types';
import { redirectMap, redirectSlugs } from '@/data/seo/redirectMap';
import { isNoindex, coreToolSlugs } from '@/data/seo/urlClassification';
// All known target-size slugs (manual + generated) ordered by size for adjacency linking
const targetSizeOrder = [
  'reducir-imagen-10kb', 'reducir-imagen-20kb', 'reducir-imagen-25kb',
  'reducir-imagen-30kb', 'reducir-imagen-50kb', 'reducir-imagen-75kb',
  'reducir-imagen-100kb', 'reducir-imagen-150kb', 'reducir-imagen-200kb',
  'reducir-imagen-250kb', 'reducir-imagen-300kb', 'reducir-imagen-500kb',
  'reducir-imagen-1mb', 'reducir-imagen-2mb', 'reducir-imagen-3mb',
  'reducir-imagen-5mb',
];

const fotoTargetSizeOrder = [
  'reducir-foto-50kb', 'reducir-foto-100kb', 'reducir-foto-200kb',
  'reducir-foto-300kb', 'reducir-foto-500kb',
];

const compressionSynonyms = [
  'comprimir-imagen', 'comprimir-foto', 'comprimir-imagen-online',
  'comprimir-imagen-sin-perder-calidad', 'comprimir-fotos-online',
  'comprimir-imagen-gratis', 'comprimir-foto-online', 'comprimir-imagen-jpg',
  'reducir-peso-imagen', 'reducir-tamano-imagen', 'reducir-peso-foto',
];

const useCaseSlugs = [
  'comprimir-imagen-para-email', 'comprimir-imagen-para-whatsapp',
  'comprimir-imagen-para-cv', 'comprimir-imagen-para-web',
  'comprimir-imagen-para-web-gratis', 'comprimir-imagen-para-formulario',
  'comprimir-imagen-para-pdf', 'comprimir-foto-whatsapp',
];

const optimizationSlugs = [
  'optimizar-imagen-online', 'optimizar-imagen-para-web',
  'optimizar-imagen-wordpress', 'optimizar-imagen-shopify',
  'optimizar-imagen-woocommerce', 'optimizar-imagen-seo',
  'optimizar-imagen-para-blog',
];

const cropSlugs = [
  'recortar-imagen', 'recortar-imagen-online', 'recortar-imagen-instagram',
  'recortar-imagen-cuadrada', 'recortar-imagen-16-9',
];

const rotateSlugs = [
  'rotar-imagen', 'girar-foto', 'voltear-imagen', 'rotar-imagen-90-grados',
];

const resizePercentSlugs = [
  'redimensionar-imagen-50-porciento', 'redimensionar-imagen-25-porciento',
  'aumentar-tamano-imagen',
];

const batchSlugs = [
  'comprimir-varias-imagenes',
];

const adjustSlugs = [
  'ajustar-brillo-imagen', 'ajustar-contraste-imagen',
  'mejorar-calidad-imagen', 'enfocar-imagen', 'suavizar-imagen',
  // Phase 4 adjust
  'quitar-ruido-imagen', 'eliminar-grano-imagen',
  'mejorar-foto-borrosa', 'aclarar-imagen-oscura',
  'mejorar-calidad-foto-online',
];

const pdfSlugs = [
  'convertir-imagen-pdf', 'imagenes-a-pdf', 'jpg-a-pdf', 'png-a-pdf',
];

const avifConvertSlugs = [
  'convertir-avif-jpg', 'convertir-avif-png',
];

// Phase 4 specific slug groups for targeted cross-linking
const phase4TargetUseCaseSlugs = [
  'reducir-imagen-para-formulario', 'reducir-imagen-para-cv',
];

const phase4ResizeUseCaseSlugs = [
  'redimensionar-imagen-para-whatsapp', 'redimensionar-imagen-para-cv',
];

function getAdjacentSlugs(slug: string, list: string[], count: number = 2): string[] {
  const idx = list.indexOf(slug);
  if (idx === -1) return [];
  const result: string[] = [];
  if (idx > 0) result.push(list[idx - 1]);
  if (idx < list.length - 1) result.push(list[idx + 1]);
  // Add one more if we only got 1
  if (result.length < count) {
    if (idx > 1) result.push(list[idx - 2]);
    else if (idx < list.length - 2) result.push(list[idx + 2]);
  }
  return result;
}

export function assignRelatedSlugs(tools: ToolDefinition[]): ToolDefinition[] {
  const allSlugs = new Set(tools.map(t => t.slug));

  return tools.map(tool => {
    // If manual tool already has relatedSlugs, keep them
    if (tool.relatedSlugs.length > 0) return tool;

    const related: string[] = [];

    // Target-size pages
    if (tool.pageType === 'target-size') {
      const isImagen = tool.slug.startsWith('reducir-imagen-');
      const isFoto = tool.slug.startsWith('reducir-foto-');

      if (isImagen) {
        related.push(...getAdjacentSlugs(tool.slug, targetSizeOrder));
        // Link to corresponding "foto" variant if exists
        const fotoVariant = tool.slug.replace('reducir-imagen-', 'reducir-foto-');
        if (allSlugs.has(fotoVariant)) related.push(fotoVariant);
      } else if (isFoto) {
        related.push(...getAdjacentSlugs(tool.slug, fotoTargetSizeOrder));
        // Link to corresponding "imagen" variant
        const imagenVariant = tool.slug.replace('reducir-foto-', 'reducir-imagen-');
        if (allSlugs.has(imagenVariant)) related.push(imagenVariant);
      }
      related.push('comprimir-imagen');
    }

    // Compression synonym pages
    if (compressionSynonyms.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = compressionSynonyms.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 3));
      if (!related.includes('comprimir-jpg')) related.push('comprimir-jpg');
    }

    // Use-case pages
    if (useCaseSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = useCaseSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 2));
      related.push('comprimir-imagen');
      if (!related.includes('comprimir-foto')) related.push('comprimir-foto');
    }

    // Optimization pages
    if (optimizationSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = optimizationSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 2));
      related.push('optimizar-imagen-online');
      if (!related.includes('comprimir-imagen-para-web')) related.push('comprimir-imagen-para-web');
    }

    // Crop pages
    if (cropSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = cropSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 2));
      if (allSlugs.has('redimensionar-imagen')) related.push('redimensionar-imagen');
      if (allSlugs.has('comprimir-imagen')) related.push('comprimir-imagen');
    }

    // Rotate pages
    if (rotateSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = rotateSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 2));
      if (allSlugs.has('recortar-imagen')) related.push('recortar-imagen');
      if (allSlugs.has('comprimir-imagen')) related.push('comprimir-imagen');
    }

    // Resize-percent pages
    if (resizePercentSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = resizePercentSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 2));
      if (allSlugs.has('redimensionar-imagen')) related.push('redimensionar-imagen');
    }

    // Batch pages
    if (batchSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      if (allSlugs.has('comprimir-imagen')) related.push('comprimir-imagen');
      if (allSlugs.has('comprimir-fotos-online')) related.push('comprimir-fotos-online');
      if (allSlugs.has('comprimir-imagen-gratis')) related.push('comprimir-imagen-gratis');
    }

    // Adjust pages
    if (adjustSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = adjustSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 2));
      if (allSlugs.has('comprimir-imagen')) related.push('comprimir-imagen');
    }

    // PDF pages
    if (pdfSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = pdfSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 2));
      if (allSlugs.has('convertir-jpg-webp')) related.push('convertir-jpg-webp');
    }

    // AVIF conversion pages
    if (avifConvertSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = avifConvertSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others);
      if (allSlugs.has('convertir-webp-jpg')) related.push('convertir-webp-jpg');
      if (allSlugs.has('convertir-png-jpg')) related.push('convertir-png-jpg');
    }

    // Phase 4: Target-size use-case pages (CV, formulario)
    if (tool.slug === 'reducir-imagen-para-cv' && tool.relatedSlugs.length === 0) {
      related.push('redimensionar-imagen-para-cv', 'comprimir-imagen', 'reducir-imagen-200kb');
      if (allSlugs.has('reducir-imagen-para-formulario')) related.push('reducir-imagen-para-formulario');
    }

    // Phase 4: Resize use-case pages
    if (tool.slug === 'redimensionar-imagen-para-whatsapp' && tool.relatedSlugs.length === 0) {
      if (allSlugs.has('comprimir-imagen-para-whatsapp')) related.push('comprimir-imagen-para-whatsapp');
      if (allSlugs.has('redimensionar-imagen-instagram')) related.push('redimensionar-imagen-instagram');
      if (allSlugs.has('comprimir-imagen')) related.push('comprimir-imagen');
    }

    if (tool.slug === 'redimensionar-imagen-para-cv' && tool.relatedSlugs.length === 0) {
      if (allSlugs.has('reducir-imagen-para-cv')) related.push('reducir-imagen-para-cv');
      if (allSlugs.has('redimensionar-imagen-pasaporte')) related.push('redimensionar-imagen-pasaporte');
      if (allSlugs.has('redimensionar-imagen-carnet')) related.push('redimensionar-imagen-carnet');
      if (allSlugs.has('comprimir-imagen')) related.push('comprimir-imagen');
    }

    // Dedupe, filter redirects/noindex, replace redirect slugs with targets, prefer core
    const cleaned = [...new Set(related)]
      .filter(s => s !== tool.slug)
      .map(s => redirectSlugs.has(s) ? redirectMap[s] : s) // resolve redirects to targets
      .filter(s => !redirectSlugs.has(s) && !isNoindex(s) && allSlugs.has(s));

    // Sort: core pages first
    const sorted = cleaned.sort((a, b) => {
      const aCore = coreToolSlugs.has(a) ? 0 : 1;
      const bCore = coreToolSlugs.has(b) ? 0 : 1;
      return aCore - bCore;
    });

    const uniqueRelated = [...new Set(sorted)].slice(0, 4);

    return { ...tool, relatedSlugs: uniqueRelated };
  });
}
